import type { ReactNode } from "react";
import { useCallback, useState } from "react";
import ReactDOM from "react-dom";
import { connect } from "react-redux";

import actions from "@/actions";
import Input from "@/components/core/layout/input";
import Loading from "@/components/core/layout/loading";

import Button from "../base/Button";
import Panel from "../base/Panel";

const renderUncatalogIcon = (color: string): ReactNode => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        width="30"
        height="30"
        viewBox="0 0 28 28"
        style={{ width: 30, height: 30 }}
    >
        <g fill="none" fillRule="evenodd" stroke={color} strokeLinecap="square">
            <polygon points="4,17 14,4 24,17" />
            <rect x="4" y="21" width="20" height="4" />
        </g>
    </svg>
);

const renderFirmwareIcon = (color: string): ReactNode => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        width="30"
        height="30"
        viewBox="0 0 28 28"
        style={{ width: 30, height: 30 }}
    >
        <g fill="none" fillRule="evenodd" stroke={color} strokeLinecap="square">
            <rect x="6" y="4" width="16" height="20" rx="2" />
            <line x1="10" y1="9" x2="18" y2="9" />
            <line x1="10" y1="13" x2="18" y2="13" />
            <line x1="10" y1="17" x2="14" y2="17" />
        </g>
    </svg>
);

interface MorePanelProps {
    /**
     * Whether any lights are currently selected.
     */
    hasSelectedLights: boolean;
    /**
     * Whether the uncatalog confirmation dialog is showing.
     */
    uncatalogConfirming: boolean;
    /**
     * Callback to toggle the uncatalog confirmation dialog.
     */
    setUncatalogConfirming: (confirming: boolean) => void;
    /**
     * Callback to execute the uncatalog action.
     */
    uncatalogLights: () => void;
    /**
     * Callback to change the map mode.
     */
    changeMode: (name: string) => void;
}

/**
 * Panel for the "more" toolbar button, displaying additional actions for selected lights.
 *
 * @param {MorePanelProps} props - The props for the MorePanel component.
 * @returns {ReactNode} The rendered more panel.
 */
const MorePanel = ({
    hasSelectedLights,
    uncatalogConfirming,
    setUncatalogConfirming,
    uncatalogLights,
    changeMode,
}: MorePanelProps): ReactNode => {
    const [confirmationText, setConfirmationText] = useState("");

    const handleUncatalog = useCallback(() => {
        setUncatalogConfirming(true);
    }, [setUncatalogConfirming]);

    const handleConfirm = useCallback(() => {
        uncatalogLights();
        setUncatalogConfirming(false);
        setConfirmationText("");
    }, [uncatalogLights, setUncatalogConfirming]);

    const handleDeny = useCallback(() => {
        setUncatalogConfirming(false);
        setConfirmationText("");
    }, [setUncatalogConfirming]);

    return (
        <Panel title="More Actions">
            <Button
                color="#FFF"
                title="Firmware"
                className="tools-bar-button"
                onClick={() => changeMode("firmware")}
                renderIcon={renderFirmwareIcon}
            />
            <Button
                color="#FFF"
                title="Uncatalog"
                className="tools-bar-button"
                onClick={handleUncatalog}
                disabled={!hasSelectedLights}
                renderIcon={renderUncatalogIcon}
            />
            {uncatalogConfirming &&
                ReactDOM.createPortal(
                    <Loading
                        type="dialog"
                        onConfirm={handleConfirm}
                        onDeny={handleDeny}
                        confirmText="Confirm"
                        denyText="Cancel"
                        confirmDisabled={confirmationText.trim() !== "Accept"}
                        message="Are you sure you want to uncatalog the selected light(s)? This action cannot be undone."
                    >
                        <Input
                            label='Type "Accept" to confirm'
                            type="text"
                            value={confirmationText}
                            onChange={setConfirmationText}
                        />
                    </Loading>,
                    document.body,
                )}
        </Panel>
    );
};

const mapStateToProps = ({
    map,
}: {
    map: {
        general: { uncatalogConfirming: boolean; selectedLights: string[] };
    };
}) => ({
    uncatalogConfirming: map.general.uncatalogConfirming,
    hasSelectedLights: map.general.selectedLights.length > 0,
});

const mapDispatchToProps = {
    setUncatalogConfirming: actions.map.general.setUncatalogConfirming,
    uncatalogLights: actions.map.general.uncatalogLights,
    changeMode: actions.map.general.changeMode,
};

export default connect(mapStateToProps, mapDispatchToProps)(MorePanel);
